import React, { useState, useEffect } from "react";
import { useQuery } from "@apollo/client";
import { withTranslation } from "react-i18next";
import { withRouter } from "react-router";
import { toast } from "react-toastify";

import { Card, Dimmer, Grid, Alert, Icon } from "tabler-react";

import GET_USER_PROFILE from "../../../../queries/system/get_user_profile";
import ShopAccountProfileBase from "../ShopAccountProfileBase";
import TelegramConnectionStatus from "./TelegramConnectionStatus";
import TelegramConnectButton from "./TelegramConnectButton";
import TelegramDisconnectButton from "./TelegramDisconnectButton";
import TelegramInstructions from "./TelegramInstructions";
import TelegramReverseConnect from "./TelegramReverseConnect";

function ShopAccountTelegram({ t, match, history, location }) {
    const [isConnecting, setIsConnecting] = useState(false);
    const [showInstructions, setShowInstructions] = useState(false);

    const [isPolling, setIsPolling] = useState(false);

    const { loading, error, data, refetch } = useQuery(GET_USER_PROFILE);

    // Check for reverse connection token in URL
    const urlParams = new URLSearchParams(location.search);
    const reverseToken = urlParams.get("token");

    // Check for URL parameters
    useEffect(() => {
        const urlParams = new URLSearchParams(location.search);
        const shouldConnect = urlParams.get("connect") === "true";

        if (shouldConnect && !isConnecting && !reverseToken) {
            // Auto-trigger connection flow (only if not reverse linking)
            handleAutoConnect();
        }
    }, [location.search, isConnecting, reverseToken]);

    // Polling for connection status
    useEffect(() => {
        let pollInterval;

        if (isPolling) {
            pollInterval = setInterval(async () => {
                try {
                    const response = await fetch("/d/api/telegram/status/", {
                        credentials: "same-origin",
                    });

                    if (response.ok) {
                        const statusData = await response.json();
                        if (statusData.connected) {
                            setIsPolling(false);
                            setShowInstructions(false);
                            setIsConnecting(false);
                            await refetch(); // Refresh user data
                            toast.success(
                                t("shop.account.telegram.connection_success"),
                                {
                                    position: toast.POSITION.BOTTOM_RIGHT,
                                },
                            );
                        }
                    }
                } catch (error) {
                    console.error("Error polling telegram status:", error);
                }
            }, 3000); // Poll every 3 seconds
        }

        return () => {
            if (pollInterval) {
                clearInterval(pollInterval);
            }
        };
    }, [isPolling, refetch, t]);

    const handleAutoConnect = () => {
        setIsConnecting(true);
        setShowInstructions(true);
    };

    const handleConnectionStart = () => {
        setIsConnecting(true);
        setShowInstructions(true);
        setIsPolling(true);
    };

    const handleConnectionComplete = () => {
        setIsConnecting(false);
        setShowInstructions(false);
        setIsPolling(false);
        refetch(); // Refresh user data
    };

    const handleDisconnectComplete = () => {
        refetch(); // Refresh user data
    };

    if (loading)
        return (
            <ShopAccountProfileBase>
                <Dimmer active={true} loader={true} />
            </ShopAccountProfileBase>
        );

    if (error)
        return (
            <ShopAccountProfileBase>
                <Alert type="danger">
                    <Icon name="alert-triangle" className="mr-2" />
                    {t("shop.account.telegram.error_loading_data")}
                </Alert>
            </ShopAccountProfileBase>
        );

    const user = data.user;
    const isConnected = !!user.telegramChatId;

    const handleReverseConnectSuccess = (data) => {
        // Refresh telegram status
        refetch();
        // Clear token from URL
        history.replace("/shop/account/telegram");
        toast.success(t("shop.account.telegram.connection_successful"));
    };

    const handleReverseConnectError = (error) => {
        console.error("Reverse connect error:", error);
    };

    // If we have a reverse token, show the reverse connect component
    if (reverseToken) {
        return (
            <ShopAccountProfileBase accountName={user.fullName}>
                <Grid.Row>
                    <Grid.Col md={12}>
                        <TelegramReverseConnect
                            token={reverseToken}
                            onSuccess={handleReverseConnectSuccess}
                            onError={handleReverseConnectError}
                        />
                    </Grid.Col>
                </Grid.Row>
            </ShopAccountProfileBase>
        );
    }

    return (
        <ShopAccountProfileBase accountName={user.fullName}>
            <Grid.Row>
                <Grid.Col md={12}>
                    <Card>
                        <Card.Header>
                            <Card.Title>
                                <Icon name="message-circle" className="mr-2" />
                                {t("shop.account.telegram.title")}
                            </Card.Title>
                        </Card.Header>
                        <Card.Body>
                            <div className="mb-4">
                                <p>{t("shop.account.telegram.description")}</p>

                                {/* Feature benefits list */}
                                <ul className="list-unstyled">
                                    {/* <li className="mb-2">
                                        <Icon
                                            name="check"
                                            className="text-success mr-2"
                                        />
                                        {t(
                                            "shop.account.telegram.benefit_instant_notifications",
                                        )}
                                    </li>
                                    <li className="mb-2">
                                        <Icon
                                            name="check"
                                            className="text-success mr-2"
                                        />
                                        {t(
                                            "shop.account.telegram.benefit_booking_confirmations",
                                        )}
                                    </li>
                                    <li className="mb-2">
                                        <Icon
                                            name="check"
                                            className="text-success mr-2"
                                        />
                                        {t(
                                            "shop.account.telegram.benefit_class_reminders",
                                        )}
                                    </li>
                                    <li className="mb-2">
                                        <Icon
                                            name="check"
                                            className="text-success mr-2"
                                        />
                                        {t(
                                            "shop.account.telegram.benefit_waitlist_updates",
                                        )}
                                    </li>
                                    */}
                                    <li className="mb-2">
                                        <Icon
                                            name="check"
                                            className="text-success mr-2"
                                        />
                                        {t(
                                            "shop.account.telegram.benefit_waitlist_updates",
                                        )}
                                    </li>
                                    <li className="mb-2">
                                        <Icon
                                            name="check"
                                            className="text-success mr-2"
                                        />
                                        {t(
                                            "shop.account.telegram.other_benefits_coming",
                                        )}
                                    </li>
                                </ul>
                            </div>

                            {/* Connection Status */}
                            <TelegramConnectionStatus
                                isConnected={isConnected}
                                telegramUsername={user.telegramUsername}
                                connectedAt={user.telegramConnectedAt}
                            />

                            {/* Connection Actions */}
                            <div className="mt-4">
                                {!isConnected ? (
                                    <TelegramConnectButton
                                        onConnectionStart={
                                            handleConnectionStart
                                        }
                                        isConnecting={isConnecting}
                                    />
                                ) : (
                                    <TelegramDisconnectButton
                                        onDisconnectComplete={
                                            handleDisconnectComplete
                                        }
                                    />
                                )}
                            </div>

                            {/* Instructions (shown during connection process) */}
                            {showInstructions && !isConnected && (
                                <div className="mt-4">
                                    <TelegramInstructions
                                        onConnectionComplete={
                                            handleConnectionComplete
                                        }
                                        isPolling={isPolling}
                                    />
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                </Grid.Col>
            </Grid.Row>
        </ShopAccountProfileBase>
    );
}

export default withTranslation()(withRouter(ShopAccountTelegram));
